// Centralized header cart-count keeper.
//
// Problem this solves (two reported bugs):
//   1. Homepage shows "0" in the cart badge after navigating back to it.
//      The homepage HTML is page-cached anonymously (Hostinger / LiteSpeed),
//      so the server-rendered badge is frozen at whatever it was when the
//      page was first cached — usually 0. Woo's own fragment script doesn't
//      reliably repaint it on a module-type build, so the badge stays wrong.
//   2. Adding a BUNDLE from the homepage didn't bump the badge at all — the
//      bundle add fired a `haegoziya:cart-updated` event that nothing listened
//      to.
//
// Fix: one module that owns the badge. It (a) hydrates the real count from a
// non-cacheable AJAX call on every page load, and (b) updates the badge after
// any cart mutation — bundle add, quick-add, drawer change, Woo add/remove.
// It does NOT depend on Woo's cart-fragments script, so it works even on a
// fully cached page.

import { getAjaxUrl } from './_ajax.js';

function getNonce() {
    return (window.HAEGOZIYA && window.HAEGOZIYA.qtyNonce) || '';
}

// Write a number into every cart badge in the DOM (header link, mini bubble,
// floating cart FAB — they all share the [data-cart-count] hook).
function applyCount(n) {
    if (typeof n !== 'number' || Number.isNaN(n) || n < 0) return;
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = String(n);
    });
}

let inFlight = false;

// Ask the server for the authoritative whole-cart item count and paint it.
// haegoziya_get_qtys returns total_qty even when no items[] are requested.
async function refresh() {
    const nonce = getNonce();
    if (!nonce || inFlight) return;
    inFlight = true;
    try {
        const body = new FormData();
        body.append('action', 'haegoziya_get_qtys');
        body.append('nonce', nonce);
        const res = await fetch(getAjaxUrl(), {
            method: 'POST',
            credentials: 'same-origin',
            cache: 'no-store',
            body,
        });
        if (!res.ok) return;
        const json = await res.json();
        if (json && json.success && json.data && typeof json.data.total_qty === 'number') {
            applyCount(json.data.total_qty);
        }
    } catch (_) {
        /* offline / stale page — leave the badge as-is */
    } finally {
        inFlight = false;
    }
}

// Bundle add (bundles.js) dispatches this with the fresh server count.
document.addEventListener('haegoziya:cart-updated', (e) => {
    const d = e.detail || {};
    const n = typeof d.cart_count === 'number' ? d.cart_count
        : (typeof d.total_qty === 'number' ? d.total_qty : null);
    if (n !== null) applyCount(n);
    else refresh();
});

// Catch every other cart mutation path (quick-add, product page, drawer,
// Woo's own add/remove) — they all fire these jQuery events on body.
function bindWoo($) {
    $(document.body).on('added_to_cart removed_from_cart updated_cart_totals', function () {
        refresh();
    });
}
if (window.jQuery) {
    bindWoo(window.jQuery);
} else {
    document.addEventListener('DOMContentLoaded', () => {
        if (window.jQuery) bindWoo(window.jQuery);
    });
}

// Hydrate on load — this is what fixes the "0 on the cached homepage" bug.
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refresh);
} else {
    refresh();
}

// Expose for other modules that already know the new count.
window.haegoziyaSetCartCount = applyCount;
